'use client';

import { useEffect, useMemo, useState, useTransition } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { apiRoutes, getApiUrl } from '@/constants/api';
import { contactInfo } from '@/constants/contact';
import { useAuth } from '@/contexts/auth-context';
import {
  BadRequestError,
  NetworkError,
  RateLimitError,
  ServerError,
} from '@/lib/api/errors/api-errors';
import { NotAuthenticatedError } from '@/lib/api/errors/not-authenticated-error';
import { clientApiFetcher } from '@/lib/api/fetcher/client';
import type { Post } from '@/types/post';

const contactType = z.enum([
  'general',
  'bug-report',
  'feature-request',
  'account-support',
  'other',
  'appeal',
]);

const contactTypeDisplayMap: Record<z.infer<typeof contactType>, string> = {
  general: 'General Inquiry',
  'bug-report': 'Bug Report',
  'feature-request': 'Feature Request',
  'account-support': 'Account Support',
  other: 'Other',
  appeal: 'Appeal',
};

const formSchema = z
  .object({
    username: z.string().min(1, {
      message: 'Name is required',
    }),
    email: z.string().email({
      message: 'Please enter a valid email address',
    }),
    type: contactType,
    subject: z
      .string()
      .min(1, {
        message: 'Subject is required',
      })
      .max(100, {
        message: 'Subject must be less than 100 characters',
      }),
    message: z
      .string()
      .min(1, {
        message: 'Message is required',
      })
      .max(500, {
        message: 'Message must be less than 500 characters',
      }),
    postId: z.string().optional(),
    userId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.type === 'appeal' && !data.postId && !data.userId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Post ID or User ID is required for appeals',
        path: ['postId'],
      });
    }
  });

type CriticalFormError = {
  title: string;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  hasCloseButton?: boolean;
};

export default function ContactFormComponent() {
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user } = useAuth();
  const [criticalError, setCriticalError] = useState<CriticalFormError | null>(
    null,
  );
  const [appealPost, setAppealPost] = useState<Post | null>(null);
  const [appealPrefillNote, setAppealPrefillNote] = useState<string | null>(
    null,
  );
  const [isAppealLoading, setIsAppealLoading] = useState(false);

  const mapTypeParam = (param: string): z.infer<typeof contactType> | '' => {
    const normalized = param.toLowerCase().trim();
    if (normalized === 'general inquiry' || normalized === 'general') {
      return 'general';
    }
    if (normalized === 'bug report' || normalized === 'bug-report') {
      return 'bug-report';
    }
    if (normalized === 'feature request' || normalized === 'feature-request') {
      return 'feature-request';
    }
    if (normalized === 'account support' || normalized === 'account-support') {
      return 'account-support';
    }
    if (normalized === 'other') {
      return 'other';
    }
    if (normalized === 'appeal' || normalized === 'moderation appeal') {
      return 'appeal';
    }
    return '';
  };

  const typeParam = searchParams.get('type') || '';
  const mappedType = typeParam ? mapTypeParam(typeParam) : '';
  const subjectParam = searchParams.get('subject') || '';
  const postIdParam = searchParams.get('postId') || '';
  const userIdParam = searchParams.get('userId') || '';
  const usernameParam = searchParams.get('username') || '';
  const emailParam = searchParams.get('email') || '';
  const appealTypeParam = searchParams.get('appealType') || '';
  const isAppealFlow = mappedType === 'appeal';
  const isAccountAppeal = isAppealFlow && !!userIdParam;
  const isPostAppeal = isAppealFlow && !!postIdParam;

  const appealTypeDisplayMap: Record<string, string> = {
    account_suspension: 'Account Suspension',
    account_ban: 'Account Ban',
    account_trash: 'Account Deletion',
    post_deletion: 'Post Deletion',
  };

  const appealTypeLabel =
    appealTypeDisplayMap[appealTypeParam] ||
    (isAccountAppeal ? 'Account Suspension' : 'Post Deletion');

  const appealSubject = useMemo(() => {
    if (isAccountAppeal) {
      return `Appeal: ${appealTypeLabel}`;
    }
    const basePrefix = 'Appeal: ';
    const maxLength = 100;
    const maxDetailLength = Math.max(0, maxLength - basePrefix.length);

    if (appealPost?.description) {
      const normalized = appealPost.description.replace(/\s+/g, ' ').trim();
      if (normalized.length <= maxDetailLength) {
        return `${basePrefix}${normalized}`;
      }
      return `${basePrefix}${normalized.slice(0, maxDetailLength - 3)}...`;
    }

    if (postIdParam) {
      return `Appeal for post ${postIdParam}`;
    }

    return 'Moderation appeal';
  }, [appealPost?.description, postIdParam, isAccountAppeal, appealTypeLabel]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      email: '',
      type: mappedType || undefined,
      subject: subjectParam || '',
      message: '',
      postId: postIdParam || undefined,
      userId: userIdParam || undefined,
    },
  });

  useEffect(() => {
    if (mappedType) {
      form.setValue('type', mappedType);
    }
    if (postIdParam) {
      form.setValue('postId', postIdParam);
    }
    if (userIdParam) {
      form.setValue('userId', userIdParam);
    }
  }, [mappedType, subjectParam, postIdParam, userIdParam, isAppealFlow, form]);

  useEffect(() => {
    if (!isAppealFlow) return;
    // Account appeals don't need post data
    if (isAccountAppeal) return;
    if (!postIdParam) {
      setAppealPrefillNote(
        'Appeal link is missing a post ID. Please contact support if this continues.',
      );
      return;
    }

    let isMounted = true;
    setIsAppealLoading(true);
    setAppealPrefillNote(null);

    clientApiFetcher<Post>(getApiUrl(apiRoutes.post.getById(postIdParam)))
      .then((post) => {
        if (!isMounted) return;
        setAppealPost(post);
      })
      .catch((error) => {
        if (!isMounted) return;
        if (error instanceof NotAuthenticatedError) {
          setAppealPrefillNote(
            'Log in to prefill your appeal details automatically.',
          );
          return;
        }
        setAppealPrefillNote(
          'We could not load your post details. You can still submit your appeal.',
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsAppealLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isAppealFlow, isAccountAppeal, postIdParam]);

  useEffect(() => {
    if (!isAppealFlow) return;

    const currentName = form.getValues('username');
    const currentEmail = form.getValues('email');
    const currentSubject = form.getValues('subject');

    if (isAccountAppeal) {
      if (!currentName && usernameParam) {
        form.setValue('username', usernameParam, { shouldDirty: false });
      }
      if (!currentEmail && emailParam) {
        form.setValue('email', emailParam, { shouldDirty: false });
      }
    } else {
      const userName = user?.fullName || user?.username || '';
      if (!currentName && userName) {
        form.setValue('username', userName, { shouldDirty: false });
      }
      if (!currentEmail && user?.email) {
        form.setValue('email', user.email, { shouldDirty: false });
      }
    }

    if (!currentSubject && appealSubject) {
      form.setValue('subject', appealSubject, { shouldDirty: false });
    }

    if (postIdParam) {
      form.setValue('postId', postIdParam, { shouldDirty: false });
    }
  }, [
    isAppealFlow,
    isAccountAppeal,
    user,
    usernameParam,
    emailParam,
    appealSubject,
    postIdParam,
    form,
  ]);

  useEffect(() => {
    if (!isAppealFlow) return;
    if (!appealPost?.user) return;

    const currentName = form.getValues('username');
    if (currentName) return;

    const postOwnerName = appealPost.user.fullName || appealPost.user.username;
    if (postOwnerName) {
      form.setValue('username', postOwnerName, { shouldDirty: false });
    }
  }, [isAppealFlow, appealPost, form]);

  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    // Remove empty string fields that could break ObjectId validation on the backend
    const cleanedPayload = Object.fromEntries(
      Object.entries(data).filter(([_, v]) => v !== '' && v !== undefined),
    );

    startTransition(async () => {
      try {
        await clientApiFetcher<{ message: string }>(
          getApiUrl(apiRoutes.contact.submit),
          {
            method: 'POST',
            body: JSON.stringify(cleanedPayload),
          },
        );

        form.reset();
        toast.success('Message sent successfully', {
          description: 'We will get back to you as soon as possible.',
          duration: 5000,
        });
        setCriticalError(null);
      } catch (error) {
        console.error('Error submitting contact form:', error);

        if (error instanceof BadRequestError) {
          const errorMessage = error.message;

          if (errorMessage.toLowerCase().includes('username is required')) {
            toast.error('Name Required', {
              description: 'Please enter your name to submit the form.',
              duration: 5000,
            });
          } else if (errorMessage.toLowerCase().includes('email is required')) {
            toast.error('Email Required', {
              description:
                'Please enter your email address to submit the form.',
              duration: 5000,
            });
          } else if (errorMessage.toLowerCase().includes('invalid email')) {
            toast.error('Invalid Email', {
              description: 'Please enter a valid email address.',
              duration: 5000,
            });
          } else if (
            errorMessage.toLowerCase().includes('message is required')
          ) {
            toast.error('Message Required', {
              description: 'Please enter your message to submit the form.',
              duration: 5000,
            });
          } else if (
            errorMessage
              .toLowerCase()
              .includes('message must be less than 500 characters')
          ) {
            toast.error('Message Too Long', {
              description: 'Your message must be less than 500 characters.',
              duration: 5000,
            });
          } else {
            toast.error('Invalid Request', {
              description: errorMessage,
              duration: 5000,
            });
          }
        } else if (error instanceof RateLimitError) {
          setCriticalError({
            title: 'Too many attempts',
            message: 'Please wait a moment before trying again.',
            action: {
              label: 'Retry',
              onClick: () => form.handleSubmit(handleSubmit)(),
            },
            hasCloseButton: true,
          });
        } else if (error instanceof ServerError) {
          toast.error('Server error occurred', {
            description: 'Something went wrong on our end. Please try again.',
            action: {
              label: 'Retry',
              onClick: () => form.handleSubmit(handleSubmit)(),
            },
            duration: 8000,
          });
        } else if (error instanceof NotAuthenticatedError) {
          setCriticalError({
            title: 'Authentication Required',
            message: 'Your session has expired. Please log in again.',
            action: {
              label: 'Go to Login',
              onClick: () => router.push('/auth/login'),
            },
          });
        } else if (error instanceof NetworkError) {
          toast.error('Connection issue', {
            description: 'Please check your internet connection and try again.',
            action: {
              label: 'Retry',
              onClick: () => form.handleSubmit(handleSubmit)(),
            },
          });
        } else {
          toast.error('Request failed', {
            description:
              error instanceof Error
                ? error.message
                : 'Please check your information and try again.',
            action: {
              label: 'Retry',
              onClick: () => form.handleSubmit(handleSubmit)(),
            },
          });
        }
      }
    });
  };

  return (
    <>
      <div className='grid grid-cols-1 gap-8 py-12 lg:grid-cols-2'>
        {/* Contact Information */}
        <div className='flex w-full flex-col justify-between gap-8 lg:max-w-[520px]'>
          <div>
            <h1 className='py-0 text-3xl font-bold text-brand-dark'>
              {isAppealFlow ? 'Submit an Appeal' : 'Contact Us'}
            </h1>
            <p className='mt-2 text-lg text-brand-dark-2'>
              {isAccountAppeal
                ? `Please explain why the ${appealTypeLabel.toLowerCase()} on your account should be reconsidered.`
                : isPostAppeal
                  ? 'Please explain why your post removal should be reconsidered.'
                  : 'Let us know how we can help.'}
            </p>
          </div>

          <div className='rounded-lg border border-brand-green bg-[#F8FAF0] p-6'>
            <h3 className='mb-4 py-0 font-poppins text-lg font-semibold text-brand-dark'>
              Contact Info:
            </h3>
            <div className='space-y-3'>
              <div>
                <span className='font-semibold text-brand-dark'>Email: </span>
                <span className='text-brand-dark'>{contactInfo.email}</span>
              </div>
              <div>
                <span className='font-semibold text-brand-dark'>Hours: </span>
                <span className='text-brand-dark'>{contactInfo.hours}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className='w-full lg:max-w-[520px]'>
          {isAppealFlow && (
            <div className='mb-6 rounded-lg border border-brand-green bg-[#F8FAF0] p-4 text-sm text-brand-dark'>
              <p className='font-semibold'>Appeal details</p>

              {isAccountAppeal && (
                <p className='mt-2 text-xs text-brand-dark-2'>
                  Action: {appealTypeLabel}
                </p>
              )}
              {isAccountAppeal && emailParam && (
                <p className='mt-2 text-xs text-brand-dark-2'>
                  Email: {emailParam}
                </p>
              )}
              {isPostAppeal && postIdParam && (
                <p className='mt-2 text-xs text-brand-dark-2'>
                  Post ID: {postIdParam}
                </p>
              )}
              {isAppealLoading && (
                <p className='mt-2 text-xs text-brand-dark-2'>
                  Loading post details...
                </p>
              )}
            </div>
          )}
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className='space-y-6'
            >
              {isAppealFlow && (
                <>
                  <input type='hidden' {...form.register('postId')} />
                  <input type='hidden' {...form.register('userId')} />
                </>
              )}
              <FormField
                control={form.control}
                name='username'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-base text-black'>
                      Your name
                    </FormLabel>
                    <FormControl>
                      <Input
                        disabled={isPending}
                        placeholder='eg. JohnDoe'
                        {...field}
                        className='h-11 border-[#BFBFBF]'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-base text-black'>
                      Email address
                    </FormLabel>
                    <FormControl>
                      <Input
                        disabled={isPending}
                        type='email'
                        placeholder='eg. johndoe@email.com'
                        {...field}
                        className='h-11 border-[#BFBFBF]'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='type'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-base text-black'>Type</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={isPending || isAppealFlow}
                      >
                        <SelectTrigger className='h-11 border-[#BFBFBF]'>
                          <SelectValue placeholder='Select a type' />
                        </SelectTrigger>
                        <SelectContent>
                          {isAppealFlow ? (
                            <SelectItem value='appeal'>
                              {contactTypeDisplayMap.appeal}
                            </SelectItem>
                          ) : (
                            <>
                              <SelectItem value='general'>
                                {contactTypeDisplayMap.general}
                              </SelectItem>
                              <SelectItem value='bug-report'>
                                {contactTypeDisplayMap['bug-report']}
                              </SelectItem>
                              <SelectItem value='feature-request'>
                                {contactTypeDisplayMap['feature-request']}
                              </SelectItem>
                              <SelectItem value='account-support'>
                                {contactTypeDisplayMap['account-support']}
                              </SelectItem>
                              <SelectItem value='other'>
                                {contactTypeDisplayMap.other}
                              </SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='subject'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-base text-black'>
                      Subject
                    </FormLabel>
                    <FormControl>
                      <Input
                        disabled={isPending}
                        placeholder={
                          isAppealFlow
                            ? 'Appeal for removed post'
                            : 'eg. General inquiry'
                        }
                        {...field}
                        className='h-11 border-[#BFBFBF]'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='message'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-base text-black'>
                      Message
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        disabled={isPending}
                        placeholder='Type your message here...'
                        className='min-h-32 resize-none border-[#BFBFBF]'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type='submit'
                size='lg'
                disabled={isPending}
                variant='default'
                className='w-full'
              >
                {isPending ? (
                  <div className='flex items-center gap-2'>
                    <div className='size-4 animate-spin rounded-full border-2 border-white/30 border-t-white' />
                    Sending...
                  </div>
                ) : (
                  'Submit'
                )}
              </Button>
            </form>
          </Form>
        </div>
      </div>

      <AlertDialog
        open={!!criticalError}
        onOpenChange={() => setCriticalError(null)}
      >
        <AlertDialogContent className='sm:max-w-md'>
          <AlertDialogHeader>
            <div className='flex items-center gap-3 pb-2'>
              <div className='flex size-10 items-center justify-center rounded-full bg-red-100'>
                <AlertCircle className='size-5 text-red-600' />
              </div>
              <AlertDialogTitle className='py-0 text-lg font-semibold text-gray-900'>
                {criticalError?.title}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className='mt-2 text-gray-600'>
              {criticalError?.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='mt-6 flex justify-end gap-3'>
            {criticalError?.hasCloseButton && (
              <Button variant='outline' onClick={() => setCriticalError(null)}>
                Close
              </Button>
            )}
            {criticalError?.action && (
              <Button
                onClick={() => {
                  criticalError.action?.onClick();
                  setCriticalError(null);
                }}
                className='bg-brand-green hover:bg-brand-green/90'
              >
                {criticalError.action.label}
              </Button>
            )}
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
